import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ExploreSearchResult } from '../../lib/exploreApi';
import { actorDetailPath, familyDetailPath, techniqueDetailPath, iocDetailPath, countryDetailPath } from '../../lib/paths';
import EntityContextMenu from '../../components/EntityContextMenu';
import type { EntityDescriptor } from '../../components/EntityContextMenu';
import { countryFlag, countryLabel } from '../../lib/countryFlag';

const IOC_CATEGORIES = new Set(['hash', 'ip', 'domain', 'url', 'email', 'filepath']);

const CATEGORY_TITLES: Record<string, string> = {
  hash: 'Top Hashes',
  ip: 'Top IPs',
  domain: 'Top Domains',
  url: 'Top URLs',
  email: 'Top Emails',
  filepath: 'Top Files',
  cve: 'Top CVEs',
  technique: 'Top TTPs',
  actor: 'Top Actors',
  family: 'Top Families',
  country: 'Top Countries',
};

interface TopCategoryCardProps {
  category: string;
  items: ExploreSearchResult[];
  onItemClick: (value: string, category: string) => void;
  expanded?: boolean;
  isLoading?: boolean;
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
}

function formatExtra(item: ExploreSearchResult): React.ReactNode | null {
  if (!item.extra) return null;
  if (item.category === 'cve') {
    const parts: React.ReactNode[] = [];
    if (item.extra.cvss_severity) {
      const sev = String(item.extra.cvss_severity).toLowerCase();
      parts.push(
        <span key="sev" className={`explore-severity-badge explore-severity-${sev}`}>
          {item.extra.cvss_score != null ? `${item.extra.cvss_score} ` : ''}
          {String(item.extra.cvss_severity)}
        </span>
      );
    }
    if (item.extra.in_cisa_kev) {
      parts.push(
        <span key="kev" className="explore-kev-badge">KEV</span>
      );
    }
    return parts.length > 0 ? <>{parts}</> : null;
  }
  if (item.category === 'technique') {
    return item.extra.attack_id ? String(item.extra.attack_id) : null;
  }
  return null;
}

function itemToDescriptor(item: ExploreSearchResult, category: string): EntityDescriptor {
  return {
    value: item.value,
    category,
    subType: item.sub_type ?? undefined,
    attackId: item.extra?.attack_id ? String(item.extra.attack_id) : undefined,
  };
}

function navigateForItem(
  navigate: ReturnType<typeof useNavigate>,
  item: ExploreSearchResult,
  category: string,
) {
  switch (category) {
    case 'technique': {
      const aid = item.extra?.attack_id ? String(item.extra.attack_id) : item.value;
      if (aid) navigate(techniqueDetailPath(aid));
      break;
    }
    case 'actor':
      navigate(actorDetailPath(item.value));
      break;
    case 'family':
      navigate(familyDetailPath(item.value));
      break;
    case 'country':
      navigate(countryDetailPath(item.value));
      break;
    default:
      navigate(iocDetailPath(item.value, category));
  }
}

interface CtxMenuState {
  descriptor: EntityDescriptor;
  position: { x: number; y: number };
  searchValue: string;
}

export default function TopCategoryCard({
  category, items, onItemClick, expanded,
  isLoading, page = 1, pageSize = 15, total, onPageChange,
}: TopCategoryCardProps) {
  const navigate = useNavigate();
  const [ctxMenu, setCtxMenu] = useState<CtxMenuState | null>(null);

  if (!items.length && !isLoading) return null;

  const rankOffset = (page - 1) * pageSize;

  const cardClass = expanded
    ? 'explore-top-card explore-top-card--expanded'
    : 'explore-top-card';

  return (
    <div className={cardClass}>
      <h3 className="explore-top-card-title">{CATEGORY_TITLES[category] || category}</h3>
      {isLoading ? (
        <div className="explore-top-card-list explore-top-card-list--expanded">
          {Array.from({ length: pageSize }).map((_, i) => (
            <div key={i} className="explore-skeleton-row" />
          ))}
        </div>
      ) : (
        <ol className={`explore-top-card-list${expanded ? ' explore-top-card-list--expanded' : ''}`}>
          {items.map((item, idx) => {
            const extra = formatExtra(item);
            const isJsx = typeof extra !== 'string';
            return (
              <li
                key={item.value}
                className="explore-top-card-item"
                onContextMenu={(e) => {
                  e.preventDefault();
                  setCtxMenu({
                    descriptor: itemToDescriptor(item, category),
                    position: { x: e.clientX, y: e.clientY },
                    searchValue: item.value,
                  });
                }}
              >
                <span className="explore-top-card-rank">{rankOffset + idx + 1}</span>
                <button
                  className="explore-top-card-value"
                  onClick={() => navigateForItem(navigate, item, category)}
                  title={item.value}
                >
                  {category === 'country' && countryFlag(item.value) && (
                    <span aria-hidden="true" style={{ marginRight: 6 }}>{countryFlag(item.value)}</span>
                  )}
                  {item.value}
                </button>
                {item.sub_type && (
                  category === 'country' ? (
                    <span className="explore-top-card-extra" title={countryLabel(item.value) ?? item.value}>
                      {item.sub_type}
                    </span>
                  ) : category === 'actor' ? (
                    <span className="explore-country-badge" title={countryLabel(item.sub_type) ?? item.sub_type}>
                      {countryFlag(item.sub_type) && <span aria-hidden="true" style={{ marginRight: 4 }}>{countryFlag(item.sub_type)}</span>}
                      {item.sub_type}
                    </span>
                  ) : (
                    <span className={`explore-result-badge explore-badge-${item.sub_type.toLowerCase()}`}>
                      {item.sub_type}
                    </span>
                  )
                )}
                {extra && (
                  isJsx
                    ? extra
                    : <span className="explore-top-card-extra">{extra}</span>
                )}
                <span
                  className="explore-top-card-count"
                  title={
                    item.doc_count > 0
                      ? `Referenced in ${item.doc_count} documents`
                      : item.extra?.usage_count
                        ? `Used by ${item.extra.usage_count} groups`
                        : `Referenced in ${item.doc_count} documents`
                  }
                >
                  {item.doc_count > 0
                    ? item.doc_count
                    : item.extra?.usage_count
                      ? `${item.extra.usage_count} groups`
                      : item.doc_count}
                </span>
              </li>
            );
          })}
        </ol>
      )}

      {expanded && total != null && total > pageSize && onPageChange && (() => {
        const totalPages = Math.ceil(total / pageSize);
        const rangeStart = (page - 1) * pageSize + 1;
        const rangeEnd = Math.min(page * pageSize, total);
        const pages: (number | '...')[] = [];
        const near = new Set<number>();
        near.add(1);
        near.add(totalPages);
        for (let p = page - 1; p <= page + 1; p++) {
          if (p >= 1 && p <= totalPages) near.add(p);
        }
        const sorted = Array.from(near).sort((a, b) => a - b);
        for (let i = 0; i < sorted.length; i++) {
          if (i > 0 && sorted[i] - sorted[i - 1] > 1) pages.push('...');
          pages.push(sorted[i]);
        }
        return (
          <div className="explore-pagination">
            <span className="explore-pagination-info">{rangeStart}–{rangeEnd} of {total}</span>
            <div className="explore-pagination-controls">
              <div className="explore-pagination-nav-group">
                <button className="explore-pagination-btn explore-pagination-btn--nav" disabled={page <= 1} onClick={() => onPageChange(1)} aria-label="First page" title="First page">‹‹</button>
                <button className="explore-pagination-btn explore-pagination-btn--nav" disabled={page <= 1} onClick={() => onPageChange(page - 1)} aria-label="Previous page" title="Previous page">‹</button>
              </div>
              <span className="explore-pagination-divider" />
              {pages.map((p, i) =>
                p === '...' ? (
                  <span key={`ellipsis-${i}`} className="explore-pagination-ellipsis">···</span>
                ) : (
                  <button
                    key={p}
                    className={`explore-pagination-btn${p === page ? ' explore-pagination-btn--active' : ''}`}
                    onClick={() => onPageChange(p)}
                    aria-label={`Page ${p}`}
                    aria-current={p === page ? 'page' : undefined}
                  >
                    {p}
                  </button>
                )
              )}
              <span className="explore-pagination-divider" />
              <div className="explore-pagination-nav-group">
                <button className="explore-pagination-btn explore-pagination-btn--nav" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} aria-label="Next page" title="Next page">›</button>
                <button className="explore-pagination-btn explore-pagination-btn--nav" disabled={page >= totalPages} onClick={() => onPageChange(totalPages)} aria-label="Last page" title="Last page">››</button>
              </div>
            </div>
          </div>
        );
      })()}

      {ctxMenu && (
        <EntityContextMenu
          descriptor={ctxMenu.descriptor}
          position={ctxMenu.position}
          onSearch={() => onItemClick(ctxMenu.searchValue, category)}
          onClose={() => setCtxMenu(null)}
        />
      )}
    </div>
  );
}
